import { useCallback, useEffect, useState } from 'react';

import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Input, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { T } from 'react-translator-component';

import * as S from './styled';

import { getUserDetail, getUserPrivateStation } from '../../../hooks/users';
import { setUsersVoucherList } from '../../../redux/actions/voucher';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import SearchCpf from '../StationForm/SearchCpf';

enum VoucherUnitStatus {
  NOT_USED = 'NOT_USED',
  SUSPEND = 'SUSPEND',
  USED = 'USED',
}

export interface VoucherUserSelectedPksWithVoucherStatus {
  user_pk: number;
  status?: VoucherUnitStatus;
}

const VoucherSelectUsersList = () => {
  const history = useHistory<{
    userSelectedPks: VoucherUserSelectedPksWithVoucherStatus[];
  }>();

  const { id } = useParams<any>();
  const dispatch = useDispatch();

  const userSelectedPks = history.location.state?.userSelectedPks;

  const userPks = useSelector((state: any) => state?.voucher?.userPks);
  const routes = useSelector((state: any) => state?.user?.routes);

  const [selectedUsers, setSelectedUsers] = useState<
    VoucherUserSelectedPksWithVoucherStatus[]
  >(userSelectedPks || userPks || []);

  const [usersDataList, setUsersDataList] = useState<any[]>(
    useSelector((state: any) => state?.voucher?.usersDataList) || [],
  );
  const [showSelect, setShowSelect] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const tryGetUserPrivateStation = useCallback(async () => {
    if (usersDataList.length) {
      return;
    }

    try {
      setLoading(true);

      const { userPrivateStationList } = await getUserPrivateStation('');

      setUsersDataList(userPrivateStationList);

      await Promise.all(
        selectedUsers.map(async (selectedUser) => {
          if (
            !userPrivateStationList.find(
              (user) => user.user_pk === selectedUser.user_pk,
            )?.email
          ) {
            const userDetail: any = await getUserDetail(selectedUser.user_pk);

            setUsersDataList((prev) => [...prev, userDetail?.userForm]);
          }
        }),
      );

      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [selectedUsers, usersDataList.length]);

  useEffect(() => {
    (async function () {
      await tryGetUserPrivateStation();
    })();
  }, [tryGetUserPrivateStation]);

  const handleCompleteSelection = () => {
    dispatch(setUsersVoucherList(selectedUsers, usersDataList));

    if (id) {
      history.push({
        pathname: `/voucher/${id}/edit`,
        state: { ...history.location.state, userSelectedPks: selectedUsers },
      });

      return;
    }

    history.goBack();
  };

  const handleChangeNewValue = (userFound) => {
    setUsersDataList((prevState) => [
      ...prevState,
      { ...userFound, hideOnList: true },
    ]);

    setSelectedUsers((prevState) => [...prevState, userFound.user_pk]);
  };

  if (loading) {
    return <ListSkeleton />;
  }

  return (
    <S.Div>
      <S.SelectContainer>
        {!showSelect && selectedUsers.length === 0 && (
          <S.EmptyContainer>
            <p>{T('No users selected')}</p>
          </S.EmptyContainer>
        )}

        <S.SelectedUsersContainer>
          {selectedUsers.length > 0 && (
            <S.SelectedUsersCount>
              {selectedUsers.length}{' '}
              {selectedUsers.length > 1 ? T('Users') : T('User')}
            </S.SelectedUsersCount>
          )}

          {(selectedUsers.length > 0 || showSelect) &&
          routes &&
          routes.find(
            ({ path }) => path === '/users_data/tenant_parent/cpf',
          ) ? (
            <SearchCpf
              chargeBoxPk={null}
              onSetNewValue={handleChangeNewValue}
            />
          ) : null}

          {selectedUsers.map((selectedUser, index) => {
            return (
              <S.SelectedItemContainer
                key={`selects-users-${index}-${Math.random()}`}
              >
                <Input
                  style={{ width: '500px', height: '40px', marginBottom: 8 }}
                  disabled
                  value={
                    usersDataList.find(
                      (user) =>
                        (user.user_pk || user?.userPk) ===
                        (selectedUser.user_pk || selectedUser),
                    )?.email
                  }
                />

                <S.ThrashButton
                  shape='circle'
                  size='large'
                  disabled={
                    selectedUser.status === VoucherUnitStatus.SUSPEND ||
                    selectedUser.status === VoucherUnitStatus.USED
                  }
                  onClick={() => {
                    setSelectedUsers((prevState) =>
                      prevState.filter((value) => value !== selectedUser),
                    );
                  }}
                >
                  <DeleteOutlined size={16} />
                </S.ThrashButton>
              </S.SelectedItemContainer>
            );
          })}

          <S.AddSelectContainer>
            {showSelect && (
              <Select
                showSearch
                style={{ width: '500px', marginBottom: 8, marginRight: 12 }}
                placeholder={T('Select users')}
                onChange={(userId: number) => {
                  setShowSelect(false);

                  setSelectedUsers((prevState) => [
                    ...prevState,
                    { user_pk: userId },
                  ]);
                }}
                filterOption={(input, option) => {
                  const user = [0, 2].map((e) => option?.children[e]).join('-');
                  return user?.toLowerCase().includes(input?.toLowerCase());
                }}
                size='large'
              >
                {usersDataList.length &&
                  usersDataList
                    .filter(
                      (user) =>
                        !selectedUsers?.some(
                          (voucherUser) => voucherUser.user_pk === user.user_pk,
                        ) &&
                        !user.hideOnList &&
                        user.tenant_related,
                    )
                    .map((user, index) => {
                      return (
                        <Select.Option value={user.user_pk} key={index}>
                          {user.email} - {user.user_name}
                        </Select.Option>
                      );
                    })}
              </Select>
            )}

            <S.RoundedButton type='primary' onClick={() => setShowSelect(true)}>
              <PlusOutlined />
            </S.RoundedButton>
          </S.AddSelectContainer>
        </S.SelectedUsersContainer>
      </S.SelectContainer>

      <S.Footer>
        <S.Button type='primary' onClick={handleCompleteSelection}>
          {T('Complete selection')}
        </S.Button>
      </S.Footer>
    </S.Div>
  );
};

export default VoucherSelectUsersList;
